import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getScopeRole } from '@/services/current-context';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { ROUTES } from '@/config/routes';
import { useNavigationService } from '@/services/navigate-service';
import { useDirtyStateBlocker } from '@/hooks/useDirtyStateBlocker';
import { showAlert } from '@/slice/AlertSlice';
import { Variants } from '@/types/common';
import { isInternalAdmin } from '@/util/roles';
import { integrationStatusCopy } from './status/copy';
import { useSyncProgress } from '../hooks/useSyncProgress';
import type {
  ConfigurationSettingsState,
  PMSVendorData,
  SettingToggleType,
} from '@/types/pms.types';
import { getVendorsListAPI } from '../service/PMSVendorsApiService';
import { testVendorConnectionSyncAPI } from '../pms-configuration/service/configurationApiService';
import {
  type ConnectionState,
  failedConnection,
  idleConnection,
} from '../pms-configuration/types/connection-state';
import { VENDOR_TAB_KEYS, type VendorTabKey } from '../pms-configuration/components/VendorTabNav';
import { type IntegrationStatus, useSaveConnectionAndSyncMutation } from '../services/pmsConnectionApi';
import type { PmsAccountRef } from '../services/pmsAccount';
import type {
  CredentialFailureKind,
  SubmittedCredentials,
  VendorDefinition,
  VendorSettingsContext,
  VendorSettingsMode,
} from './types';

const DEFAULT_SETTINGS: ConfigurationSettingsState = {
  autoSyncAssets: true,
  watermarkedPhotos: false,
};

// Module-level so it's the same reference across renders — a fresh literal
// here every render would churn the `ctx` memo below.
const NONE_STATUS: IntegrationStatus = { kind: 'none' };

/**
 * Folds a save's response body onto the previously-known vendor record,
 * defaulting to empty credential fields when nothing has been loaded yet
 * (first-connection save, before any bootstrap fetch resolved).
 */
const mergeVendorData = (
  prev: PMSVendorData | null,
  next: PMSVendorData,
): PMSVendorData => {
  const base = prev ?? { url: '', key: '', secret: '' };
  // DV-3414: a blank key/secret on save means "keep what is stored", and the server
  // kept it. Keep the (masked) record here too: an empty secret would read as an OAuth
  // connection to the AppFolio panel, and the forms would re-require both fields, until
  // the next reload.
  return {
    ...base,
    ...next,
    key: next.key || base.key,
    secret: next.secret || base.secret,
  };
};

/**
 * Band-aid: saving a paused integration's settings borrows POST …/sync, the
 * only endpoint that persists a connection. property-service runs
 * saveOrUpdateConnection before the vendor handler's paused gate, and that
 * gate is its only 409, so a 409 here means "saved, sync skipped". An error
 * before the save comes back as 500.
 *
 * Proper fix: a save-only connection endpoint in property-service. Until it
 * exists, this depends on that save-then-gate order. Banked in
 * docs/engineering-backlog.md (Backend asks, "A save-only PMS connection endpoint").
 */
const savedWhilePaused = (
  result: { data: unknown } | { error: FetchBaseQueryError | SerializedError },
): boolean =>
  'data' in result || ('status' in result.error && result.error.status === 409);

const settingsFrom = (data: PMSVendorData): ConfigurationSettingsState => ({
  autoSyncAssets: data.autoSyncAssets ?? false,
  watermarkedPhotos: data.watermarkedPhotos ?? false,
});

const applyToggle = (
  edited: ConfigurationSettingsState,
  type: SettingToggleType,
): ConfigurationSettingsState => {
  switch (type) {
    case 'autoSync':
      return { ...edited, autoSyncAssets: !edited.autoSyncAssets };
    case 'waterMarkPhotos':
      return { ...edited, watermarkedPhotos: !edited.watermarkedPhotos };
    default:
      // `SettingToggleType` is a compile-time guarantee only. The three source
      // pages used if/else-if chains that wrote back an unchanged copy for an
      // unrecognized type (pinned by VendorSettingsPage.appfolio.test.tsx's
      // "treats an unrecognized toggle type as a no-op"), so this must stay a no-op.
      return edited;
  }
};

export interface UseVendorSettingsResult {
  ctx: VendorSettingsContext;
  selectedTab: VendorTabKey;
  selectTab: (key: VendorTabKey) => void;
  isConfigLinkClicked: boolean;
  handleToggle: (type: SettingToggleType) => void;
  /** Set when `vendor.toggleGuard` intercepted a toggle; drives the confirm modal. */
  pendingToggle: SettingToggleType | null;
  confirmPendingToggle: () => void;
  cancelPendingToggle: () => void;
  blocker: ReturnType<typeof useDirtyStateBlocker>;
  /** Credential connection test in flight; the unsaved-changes bar shows "Validating…". */
  isSaving: boolean;
  onBack: () => void;
}

/**
 * Shared orchestration for every vendor settings page. Owns the credentialed
 * bootstrap, settings + dirty state, the read/edit mode (spec §5.1) and the
 * combined dirty flag, the save-and-sync call (stays on the page; progress via
 * SyncProgressBanner) and tab selection. The page owns the access gate
 * (`hasVendorAccess`). Vendor-specific behavior comes from `vendor`.
 */
export function useVendorSettings(
  vendor: VendorDefinition,
  options: { integrationStatus?: IntegrationStatus; account: PmsAccountRef },
): UseVendorSettingsResult {
  const { account } = options;
  const dispatch = useDispatch();
  const { navigateTo } = useNavigationService();
  const [searchParams] = useSearchParams();
  const integrationStatus = options.integrationStatus ?? NONE_STATUS;
  // Firing-only instance: the page renders its own subscribed SyncProgressBanner.
  const { saveAndSync: fireSaveAndSync } = useSyncProgress({ origin: 'settings', account, skip: true });
  // Called directly, not through useSyncProgress: a paused integration never syncs.
  const [saveConnection] = useSaveConnectionAndSyncMutation();

  const shouldBootstrap = vendor.shouldBootstrap?.(searchParams) ?? true;

  const [vendorData, setVendorData] = useState<PMSVendorData | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(shouldBootstrap);
  const [connectionState, setConnectionState] = useState<ConnectionState>(idleConnection());
  const [connected, setConnected] = useState(true);
  const [settings, setSettings] = useState<ConfigurationSettingsState>(DEFAULT_SETTINGS);
  const [editedSettings, setEditedSettings] =
    useState<ConfigurationSettingsState>(DEFAULT_SETTINGS);
  const [toggleLoader, setToggleLoader] = useState(false);
  const [isPanelBusy, setPanelBusy] = useState(false);
  const [formSubmitAttempted, setFormSubmitAttempted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<SettingToggleType | null>(null);
  // Read-only by default once a record exists; first connection (or Buildium's
  // ?token= return, which skips the bootstrap) opens straight into edit — there
  // is nothing to read yet. Set from the bootstrap result, not derived from
  // vendorData: the AppFolio views replace vendorData on mount and a save
  // merges into it, neither of which should flip the mode.
  const [mode, setMode] = useState<VendorSettingsMode>('edit');
  const [credentialsDirty, setCredentialsDirty] = useState(false);
  const [credentialsFormKey, setCredentialsFormKey] = useState(0);
  const [pendingCredentials, setPendingCredentials] = useState<SubmittedCredentials | null>(null);
  const [credentialFailure, setCredentialFailure] = useState<CredentialFailureKind | null>(null);
  const [selectedTab, setSelectedTab] = useState<VendorTabKey>(() => {
    const tab = searchParams.get('Tab');
    return tab !== null ? (tab as VendorTabKey) : VENDOR_TAB_KEYS.CREDENTIALS;
  });
  const [isConfigLinkClicked, setIsConfigLinkClicked] = useState(false);
  const submitRef = useRef<(() => void) | null>(null);

  const settingsDirty = JSON.stringify(editedSettings) !== JSON.stringify(settings);
  // Spec §5.4: the credentials form's dirtiness is reported upward, never
  // merged into the settings baseline (spec §2 decision 8).
  //
  // Gated on `mode`: a read view has nothing dirty by definition, and
  // `settingsDirty` alone is not a reliable "the user changed something"
  // signal. `ConfigurationSetting`'s DV-1958 effect forces
  // `watermarkedPhotos` off whenever auto-sync is off or the org has no
  // watermark on file, and that write is NOT suppressed by `isDisabled` — so
  // a stored record with an invalid combination (watermarking on, auto-sync
  // off) would otherwise arm the route-leave blocker and render the
  // unsaved-changes bar on a page the user only looked at, with a null
  // (Rentvine: the form never mounted) or stale (AppFolio admin) `submitRef`.
  // Accepted side effect: entering edit on such a record shows the bar
  // immediately, because the normalizer has already corrected an invalid
  // stored combination and that correction is genuinely unsaved.
  //
  // `pendingCredentials` is the third term: after a rejected save the
  // credentials form is remounted seeded from it, so Formik reports `dirty:
  // false` against its own new initial values and `credentialsDirty` goes back
  // to false. On a credentials-only edit `settingsDirty` is false too (the
  // baseline was rolled back to what was attempted), so without this the bar
  // would never return and a failed save would leave no way to retry it
  // (PR 3 review finding 1). Cleared on discard, on leaving edit, and on any
  // save that lands.
  const isDirty =
    mode === 'edit' && (settingsDirty || credentialsDirty || pendingCredentials !== null);
  const blocker = useDirtyStateBlocker(isDirty);

  // Baseline changes must move `settings` and `editedSettings` in the SAME
  // commit (see resetSettingsBaseline). Syncing them with a catch-up effect
  // committed one render where only `settings` had moved: isDirty was
  // spuriously true (a Back click in that frame hit the discard dialog) and
  // the late-flushing effect could stomp a toggle made just after bootstrap.
  // Invisible on a fast machine; the CPU-starved CI agent hit both
  // (QA deploy failures, 2026-09-01).
  const resetSettingsBaseline = useCallback((next: ConfigurationSettingsState) => {
    setSettings(next);
    setEditedSettings(next);
  }, []);

  useEffect(() => {
    if (!shouldBootstrap) return;
    let cancelled = false;
    const loadVendorRecord = async () => {
      try {
        const response = await getVendorsListAPI(account, vendor.key);
        const record: PMSVendorData | undefined = response?.data?.[0];
        if (!cancelled && record) {
          setVendorData(record);
          resetSettingsBaseline(settingsFrom(record));
          setMode('read');
        }
      } catch {
        // Vendor lookup failure leaves the page on its defaults (today's behavior).
      } finally {
        if (!cancelled) setIsBootstrapping(false);
      }
    };
    loadVendorRecord();
    return () => {
      cancelled = true;
    };
    // `shouldBootstrap` is derived from the mount-time search params, which the
    // vendor pages never re-read (Buildium strips its ?token= with
    // history.replaceState, which react-router doesn't observe).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, vendor.key]);

  const seedSettings = useCallback(
    (data: PMSVendorData) => resetSettingsBaseline(settingsFrom(data)),
    [resetSettingsBaseline],
  );

  const enterEdit = useCallback(() => {
    setCredentialFailure(null);
    setPendingCredentials(null);
    setMode('edit');
  }, []);

  // Leaving edit is only meaningful once a record exists — on a first
  // connection there is nothing to read, so the mode stays 'edit'. The
  // credentials form unmounts on the way out (or is about to be saved), so its
  // reported dirtiness is dropped here rather than waiting on
  // FormikDirtyReporter's unmount cleanup: isDirty feeds useDirtyStateBlocker,
  // and a stale `true` would leave the navigation guard armed.
  const exitEdit = useCallback(() => {
    setCredentialFailure(null);
    setCredentialsDirty(false);
    setPendingCredentials(null);
    if (vendorData) setMode('read');
  }, [vendorData]);

  // Discard = both halves back to baseline. Settings: editedSettings ← settings
  // (the baseline itself does not move, so this is NOT resetSettingsBaseline).
  // Credentials: the form is remounted via credentialsFormKey so a
  // first-connection discard clears typed fields; with a record the mode flips
  // to read and the form unmounts anyway (FormikDirtyReporter's cleanup then
  // lowers credentialsDirty on its own — the explicit reset below covers the
  // first-connection case where the form stays mounted).
  const discardEdits = useCallback(() => {
    setEditedSettings(settings);
    setCredentialsFormKey((k) => k + 1);
    setCredentialsDirty(false);
    setCredentialFailure(null);
    setPendingCredentials(null);
    if (vendorData) setMode('read');
  }, [settings, vendorData]);

  // Save-and-sync no longer navigates to /properties. property-service
  // persists the body (saveOrUpdateConnection) and starts the sync in one
  // call; progress shows in place via SyncProgressBanner. The baseline moves
  // through resetSettingsBaseline so isDirty never flickers true after a save.
  //
  // The baseline moves optimistically — the sync can run for minutes and a
  // 504 still means the body persisted, so awaiting the request is wrong. But
  // POST …/sync IS the persisting call: a fast rejection (500, network drop)
  // means nothing was saved, so the baseline is put back where it was — the
  // form reads dirty again, the unsaved-changes guard re-arms — and the user
  // is told. Without that, a failed save would read as a successful one.
  const saveAndSync = useCallback(
    (configData: PMSVendorData, submittedCredentials?: SubmittedCredentials) => {
      const baselineBeforeSave = settings;
      const vendorDataBeforeSave = vendorData;
      const attempted = settingsFrom(configData);
      fireSaveAndSync(configData).then((outcome) => {
        if (outcome !== 'rejected') return;
        // Nothing persisted (spec §5.6 last row): put the baseline AND the
        // record back, re-open edit so the guard re-arms and the bar returns.
        setSettings(baselineBeforeSave);
        setEditedSettings(attempted);
        setVendorData(vendorDataBeforeSave);
        // The credentials half of the rollback (PR 3 review finding 1). Blanks
        // are sent as typed (DV-3414), and the server keeps the stored credential
        // for a blank or masked value — so a blank retry would report success
        // while silently dropping what the user pasted. The submitted values are
        // therefore held and the form is remounted seeded from them. They also
        // keep `isDirty` true on a credentials-only edit, where the settings
        // baseline rolls back to something deep-equal and nothing else would
        // bring the bar back.
        if (submittedCredentials) {
          setPendingCredentials(submittedCredentials);
          setCredentialsFormKey((k) => k + 1);
        }
        setMode('edit');
        dispatch(showAlert({ type: Variants.ERROR, message: integrationStatusCopy.toasts.saveFailed }));
      });
      setVendorData((prev) => mergeVendorData(prev, configData));
      resetSettingsBaseline(attempted);
      setCredentialFailure(null);
      setCredentialsDirty(false);
      setPendingCredentials(null);
      setMode('read');
      setIsSaving(false);
      setToggleLoader(false);
    },
    [dispatch, fireSaveAndSync, resetSettingsBaseline, settings, vendorData],
  );

  // Shared failed-connection handling for both the normal save-and-sync path
  // (submitCredentials) and the disabled-integration save-without-sync path
  // (saveWithoutSync) — extracted from submitCredentials' original catch body.
  const handleConnectionFailure = useCallback(
    (error: unknown) => {
      const status = (error as { response?: { status?: number } }).response?.status;
      if (status) {
        // `editedSettings` is deliberately NOT reset to the baseline: nothing
        // was sent, so the user's edits are still unsaved truth — and with the
        // bar rendered only while dirty (spec §5.5), resetting them would take
        // the retry affordance away with them. Supersedes PR 3 planning ruling
        // 4; see the spec §5.6 Note (2026-09-11, PR 3 final review).
        setIsSaving(false);
        setToggleLoader(false);
        setConnected(false);
        setConnectionState(failedConnection());
        // Spec §5.6: the failure is rendered inline by the layout
        // (ConnectionFailedAlert); the duplicate toast is gone. The header's
        // failed state above is unchanged.
        setCredentialFailure(vendorData ? 'update' : 'first');
      }
      // No response status (network error): today's pages leave the overlay up. Preserved.
    },
    [vendorData],
  );

  // DV-2549: while the integration is disabled, saving commits the edited
  // settings without navigating to /properties (no sync to kick off) and
  // without the sync-navigation overlay — just a "Changes saved" toast.
  // The connection test runs first so credential failures surface exactly as
  // on the enabled path; it persists nothing, so the save is the …/sync call.
  const saveWithoutSync = useCallback(
    async (values: PMSVendorData) => {
      setToggleLoader(true);
      try {
        const response = await testVendorConnectionSyncAPI(
          account,
          values,
          editedSettings.autoSyncAssets,
          editedSettings.watermarkedPhotos,
          vendor.key,
        );
        const saved =
          response?.data?.status === 'Success' &&
          savedWhilePaused(
            await saveConnection({ account, body: { ...values, ...editedSettings, pmsSource: vendor.key } }),
          );
        if (saved) {
          setSettings(editedSettings); // commit: clears isDirty without a remount
          setVendorData((prev) => mergeVendorData(prev, values));
          setCredentialFailure(null);
          setCredentialsDirty(false);
          setPendingCredentials(null);
          setMode('read');
          dispatch(showAlert({ type: Variants.SUCCESS, message: integrationStatusCopy.toasts.changesSaved }));
        } else {
          // Non-Success test response or a failed save, no thrown error:
          // surface a generic failure rather than leaving the save silently no-op'd.
          dispatch(showAlert({ type: Variants.ERROR, message: integrationStatusCopy.toasts.saveFailed }));
        }
      } catch (error: unknown) {
        handleConnectionFailure(error);
      } finally {
        setToggleLoader(false);
      }
    },
    [account, dispatch, editedSettings, handleConnectionFailure, saveConnection, vendor.key],
  );

  const submitCredentials = useCallback(
    async (values: PMSVendorData) => {
      // Spec §2 decision 5: blank key/secret mean "keep what is stored". DV-3414: the
      // server decides that from its own record now. The stored credentials no longer
      // reach the browser (GET …/connect returns them masked), so there is nothing here
      // to merge back — exactly what was typed goes to the test call and the save.
      if (integrationStatus.kind === 'disabled') return saveWithoutSync(values);
      setIsSaving(true);
      setToggleLoader(true);
      try {
        const response = await testVendorConnectionSyncAPI(
          account,
          values,
          editedSettings.autoSyncAssets,
          editedSettings.watermarkedPhotos,
          vendor.key,
        );
        if (response?.data?.status === 'Success') {
          // The rollback must re-seed the form with what the user actually
          // typed, so a field left blank stays blank and no stored secret is
          // ever written back into a visible input.
          saveAndSync({ ...values, ...editedSettings, pmsSource: vendor.key }, {
            url: values.url,
            key: values.key,
            secret: values.secret,
          });
          return;
        }
        setIsSaving(false);
        setToggleLoader(false);
      } catch (error: unknown) {
        handleConnectionFailure(error);
      }
    },
    [
      account,
      editedSettings,
      handleConnectionFailure,
      integrationStatus.kind,
      saveAndSync,
      saveWithoutSync,
      vendor.key,
    ],
  );

  const requestSubmit = useCallback(() => {
    setFormSubmitAttempted(true);
    submitRef.current?.();
  }, []);

  const handleToggle = useCallback(
    (type: SettingToggleType) => {
      if (vendor.toggleGuard?.(type, editedSettings)) {
        setPendingToggle(type);
        return;
      }
      setEditedSettings((prev) => applyToggle(prev, type));
    },
    [editedSettings, vendor],
  );

  const confirmPendingToggle = useCallback(() => {
    if (pendingToggle) setEditedSettings((prev) => applyToggle(prev, pendingToggle));
    setPendingToggle(null);
  }, [pendingToggle]);

  const cancelPendingToggle = useCallback(() => setPendingToggle(null), []);

  const selectTab = useCallback((key: VendorTabKey) => {
    setSelectedTab(key);
    if (key !== VENDOR_TAB_KEYS.OVERVIEW) setIsConfigLinkClicked(false);
  }, []);

  const redirectToHowToConfigure = useCallback(() => {
    setIsConfigLinkClicked(true);
    setSelectedTab(VENDOR_TAB_KEYS.OVERVIEW);
  }, []);

  const onBack = useCallback(() => navigateTo(ROUTES.INTEGRATIONS), [navigateTo]);

  const ctx = useMemo<VendorSettingsContext>(
    () => ({
      vendor,
      account,
      vendorData,
      setVendorData,
      isBootstrapping,
      connectionState,
      setConnectionState,
      connected,
      setConnected,
      settings,
      editedSettings,
      setEditedSettings,
      seedSettings,
      isDirty,
      mode,
      enterEdit,
      exitEdit,
      discardEdits,
      setCredentialsDirty,
      credentialsFormKey,
      pendingCredentials,
      credentialFailure,
      toggleLoader,
      setToggleLoader,
      isPanelBusy,
      setPanelBusy,
      formSubmitAttempted,
      submitRef,
      requestSubmit,
      submitCredentials,
      saveAndSync,
      saveWithoutSync,
      redirectToHowToConfigure,
      isInternalAdmin: isInternalAdmin(getScopeRole()),
      integrationStatus,
    }),
    [
      vendor,
      account,
      vendorData,
      isBootstrapping,
      connectionState,
      connected,
      settings,
      editedSettings,
      seedSettings,
      isDirty,
      mode,
      enterEdit,
      exitEdit,
      discardEdits,
      credentialsFormKey,
      pendingCredentials,
      credentialFailure,
      toggleLoader,
      isPanelBusy,
      formSubmitAttempted,
      requestSubmit,
      submitCredentials,
      saveAndSync,
      saveWithoutSync,
      redirectToHowToConfigure,
      integrationStatus,
    ],
  );

  return {
    ctx,
    selectedTab,
    selectTab,
    isConfigLinkClicked,
    handleToggle,
    pendingToggle,
    confirmPendingToggle,
    cancelPendingToggle,
    blocker,
    isSaving,
    onBack,
  };
}
